"use client";

import { useQuery_experimental, type UseQueryResult } from "convex/react";
import { useMemo } from "react";
import { useQueryNow } from "@/hooks/use-now";
import { useOptionalNowQuery } from "@/hooks/use-now-query";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { DashboardAudience } from "./navigation/account-type-policy";
import { isFreightCarrierAccount } from "./navigation/account-type-policy";

function loadedCount<Value>(result: UseQueryResult<Value>): Value | undefined {
  return result.status === "success" ? result.data : undefined;
}

export function useOpenQuoteCount(enabled: boolean): number | undefined {
  const queryNow = useQueryNow();
  return useOptionalNowQuery(
    api.quotes.dashboardStatus.openCountForBuyer,
    enabled && queryNow !== undefined ? { now: queryNow } : "skip",
  );
}

export function useNavBadgeCounts(
  companyId: Id<"companies"> | undefined,
  audience: DashboardAudience,
) {
  const hasMarketplaceAccount = audience.accountTypes.some(
    (accountType) => accountType !== "freight_carrier",
  );
  const hasFreightAccount = isFreightCarrierAccount(audience);
  const lowStock = loadedCount(useQuery_experimental({
    query: api.dashboards.notifications.getLowStockBadgeCount,
    args: companyId && hasMarketplaceAccount ? { companyId } : "skip",
  }));
  const openQuotes = useOpenQuoteCount(Boolean(companyId) && hasMarketplaceAccount);
  const carrierCounts = loadedCount(useQuery_experimental({
    query: api.freight.carrierDashboard.getCarrierNavCounts,
    args: hasFreightAccount ? {} : "skip",
  }));

  return useMemo(() => {
    const badgeCounts: Record<string, number> = {};
    if (lowStock) {
      badgeCounts["/dashboard/inventory"] = lowStock;
    }
    if (openQuotes) {
      badgeCounts["/dashboard/orders"] = openQuotes;
    }
    if (carrierCounts) {
      if (carrierCounts.activeShipments.count) {
        badgeCounts["/dashboard/carrier/loads"] = carrierCounts.activeShipments.count;
      }
      if (carrierCounts.pendingBids.count) {
        badgeCounts["/dashboard/carrier/bids"] = carrierCounts.pendingBids.count;
      }
    }
    return badgeCounts;
  }, [carrierCounts, lowStock, openQuotes]);
}
